import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  CheckboxGroup,
  Checkbox,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { useSWRConfig } from "swr"
import useSWRMutation from 'swr/mutation'
import { useUser } from 'src/context';
import { postNominate } from 'src/api';
import { firstRoundCandidates } from './static';

const NominateDialog = ({ isOpen, onClose, sectionKey }) => {
  const category = firstRoundCandidates[sectionKey];
  
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [pendingData, setPendingData] = useState(null);

  const { isLogin, isLoadingUser, user: { customId } } = useUser();
  const toast = useToast();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      checkedItems: [],
      customItems: [{ value: '' }],
    },
  });

  const { fields, append } = useFieldArray({
    control,
    name: 'customItems',
  });

  const checkedItems = watch('checkedItems', []);
  const customItems = watch('customItems', []);
  const totalNominations = (checkedItems?.length || 0) + (customItems?.filter(item => item.value.trim() !== '').length || 0);
  
  // 檢查是否有任何內容被選取或輸入
  const hasContent = checkedItems.length > 0 || customItems.some(item => item.value.trim() !== '');

  const { mutate } = useSWRConfig();
  const { trigger, isMutating } = useSWRMutation(
    isLogin ? `nominate-${sectionKey}-${customId}` : null, 
    async (url, { arg }) => {
      return postNominate(arg);
    }, 
    {
      onSuccess: (res) => {
        // 因目前 axios 的寫法一定會 return res.data，所以無法用 onError 來處理錯誤
        // console.log('onSuccess', res);

        mutate(`goty2024-voteStatus`);

        setShowConfirmDialog(false);
        setPendingData(null);
        onClose();

        if (res.status === 200) {
          toast({
            title: '提名成功',
            description: '您的提名已成功送出',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } else {
          const errorMessage = res.data.result || '請稍候再試';

          toast({
            title: '送出失敗',
            description: errorMessage,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      },
    }
  );

  useEffect(() => {
    if (isOpen) {
      reset({
        checkedItems: [],
        customItems: [{ value: '' }],
      });
    }
  }, [isOpen, sectionKey, reset]);

  const onSubmit = async (data) => {
    if (!isLogin || isLoadingUser) {
      setError('root.serverError', {
        type: 'custom',
        message: '請先登入',
      });
      return;
    }

    const { checkedItems, customItems } = data;
    // TODO: 確認 customItems 的值是否安全，避免 XSS 攻擊
    const finalCustomItems = customItems.map(item => item.value.trim()).filter(Boolean);
    const total = checkedItems.length + finalCustomItems.length;

    if (total === 0) {
      setError('root.serverError', {
        type: 'custom',
        message: '請至少提名一個項目',
      });
      return;
    }

    if (total > category.limit) {
      setError('root.serverError', {
        type: 'custom',
        message: `提名數量超過上限，此項目最多只能提名 ${category.limit} 個。`,
      });
      return;
    }

    const payload = {
      section: sectionKey,
      poll: checkedItems.concat(finalCustomItems)
    }

    // 顯示確認對話框
    setPendingData(payload);
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    await trigger(pendingData);
  };

  const handleCloseAttempt = () => {
    if (hasContent) {
      setShowCloseConfirmDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirmDialog(false);
    reset();
    onClose();
  };

  if (!category) {
    return null;
  }

  const canAddMore = totalNominations < category.limit;

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleCloseAttempt} size="xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>{category.name}</ModalHeader>
          <ModalCloseButton onClick={handleCloseAttempt} />
          <ModalBody>
            <p className="mb-4 font-bold">{category.description}</p>
            <p className="mb-4 text-sm text-gray-500">候選名單為本站有上架且累計評價達一定數量的遊戲，但您可以在下方自行輸入不在此列但您認為應該入選的遊戲。</p>
            <p className="font-bold mb-2">
              提名上限: {category.limit} (目前已選 {totalNominations} 款，包含自訂提名)
            </p>
            {errors.root?.serverError && (
              <Text color="red.500" mt={2}>{errors.root.serverError.message}</Text>
            )}

            <FormControl isInvalid={!!errors.checkedItems}>
              <FormLabel fontWeight="bold" mt={4} mb={2}>從候選名單中選擇：（手機版請滑動查看）</FormLabel>
              <Controller
                name="checkedItems"
                control={control}
                render={({ field }) => (
                  <CheckboxGroup {...field}>
                    <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                      {category.candidates.map((candidate) => (
                        <Checkbox
                          key={candidate}
                          value={candidate}
                          isDisabled={!field.value.includes(candidate) && !canAddMore}
                        >
                          {candidate}
                        </Checkbox>
                      ))}
                    </div>
                  </CheckboxGroup>
                )}
              />
              <FormErrorMessage>{errors.checkedItems?.message}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="bold" mt={6} mb={2}>或自行輸入：</FormLabel>
              <p className="mb-4 text-sm text-gray-500">提醒：若您提名的遊戲主題不在 2024 年間首次推出，此提名會被視為無效；重複提名相同遊戲也會被視為無效。</p>
              <div className="flex flex-col gap-2">
                {fields.map((field, index) => (
                  <Controller
                    key={field.id}
                    name={`customItems.${index}.value`}
                    control={control}
                    render={({ field: controllerField }) => (
                      <Input
                        {...controllerField}
                        placeholder={`自行提名 ${index + 1}`}
                        isDisabled={controllerField.value === '' && !canAddMore}
                        maxLength={20}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (canAddMore) {
                              append({ value: '' });
                            }
                          }
                        }}
                      />
                    )}
                  />
                ))}
                {canAddMore && (
                  <Button onClick={() => append({ value: '' })} size="sm" mt={2}>
                    新增自訂欄位
                  </Button>
                )}
              </div>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseAttempt}>
              取消
            </Button>
            <Button colorScheme="blue" type="submit" isLoading={isSubmitting || isMutating}>
              送出提名
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              確認提名
            </AlertDialogHeader>

            <AlertDialogBody>
              您確定要送出提名嗎？送出後將無法修改。
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={() => setShowConfirmDialog(false)}>
                取消
              </Button>
              <Button colorScheme="blue" onClick={handleConfirmSubmit} ml={3} isLoading={isMutating}>
                確認送出
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={showCloseConfirmDialog} onClose={() => setShowCloseConfirmDialog(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              確認關閉
            </AlertDialogHeader>

            <AlertDialogBody>
              您有未送出的內容，確定要關閉視窗嗎？關閉後內容將會遺失。
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={() => setShowCloseConfirmDialog(false)}>
                取消
              </Button>
              <Button colorScheme="red" onClick={handleConfirmClose} ml={3}>
                確定關閉
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default NominateDialog;
